import { useState, useRef, useCallback, useEffect } from 'react';
import { searchEquipment } from '../services/equipmentService';

/**
 * Hook para gerenciar busca de equipamentos
 * Migrado de App.js para estrutura modular
 */
export const useEquipmentSearch = () => {
  const [serialSearch, setSerialSearch] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [suppressNoResultsMessage, setSuppressNoResultsMessage] = useState(false);
  
  // Refs para controle de estado
  const isEditingStep1Ref = useRef(false);
  const previousEquipmentRef = useRef(null);
  const searchInitiatedByEditRef = useRef(false);
  const lastSuccessfulSearchRef = useRef(null);
  const equipmentCacheRef = useRef({ prefix: '', type: '', allResults: [] });
  const serialInputRef = useRef(null);

  /**
   * Filtra resultados do cache localmente
   */
  const filterFromCache = useCallback((searchTerm) => {
    const cleanSearch = searchTerm.trim().toUpperCase();
    const cached = equipmentCacheRef.current.allResults || [];
    
    const filtered = cached.filter(item => {
      const serie = (item.serie || '').toUpperCase();
      const patrimonio = (item.patrimonio || '').toUpperCase();
      return serie.includes(cleanSearch) || patrimonio.includes(cleanSearch);
    });
    
    setSuggestions(filtered);
    setHasSearched(true);
    
    if (filtered.length > 0) {
      lastSuccessfulSearchRef.current = {
        search: cleanSearch,
        timestamp: Date.now()
      };
    }
  }, []);

  /**
   * Busca equipamentos no servidor
   */
  const searchSerialNumber = useCallback(async (searchTerm, type) => {
    if (!searchTerm || !type) return;
    
    const cleanSearch = searchTerm.trim().toUpperCase();
    if (cleanSearch.length < 4) {
      setSuggestions([]);
      setHasSearched(false);
      return;
    }
    
    // Pegar os primeiros 4 caracteres como prefixo
    const prefix = cleanSearch.substring(0, 4);
    
    // Se já temos cache para este prefixo e tipo, filtrar localmente
    if (equipmentCacheRef.current.prefix === prefix && equipmentCacheRef.current.type === type) {
      console.log('💾 Usando cache para prefixo:', prefix);
      filterFromCache(cleanSearch);
      return;
    }
    
    // Caso contrário, buscar no servidor
    setIsSearching(true);
    if (!isEditingStep1Ref.current) {
      setHasSearched(false);
    }
    
    try {
      const result = await searchEquipment(prefix, type, true);
      
      if (result.success) {
        // Armazenar TODOS os resultados no cache
        equipmentCacheRef.current = {
          prefix: prefix,
          type: type,
          allResults: result.suggestions || []
        };
        
        console.log('💾 Cache atualizado. Prefixo:', prefix, 'Tipo:', type, 'Total no cache:', equipmentCacheRef.current.allResults.length);
        
        // Filtrar localmente para mostrar apenas os relevantes
        filterFromCache(cleanSearch);
      } else {
        const stillMatchesPrevious = previousEquipmentRef.current && 
          cleanSearch === (previousEquipmentRef.current.serie || '').trim().toUpperCase();
        if (!stillMatchesPrevious) {
          setSuggestions([]);
          setHasSearched(true);
        }
      }
    } catch (error) {
      console.error('❌ Erro ao buscar série:', error);
      const stillMatchesPrevious = previousEquipmentRef.current && 
        cleanSearch === (previousEquipmentRef.current.serie || '').trim().toUpperCase();
      if (!stillMatchesPrevious) {
        setSuggestions([]);
        setHasSearched(true);
      }
    } finally {
      setIsSearching(false);
      if (searchInitiatedByEditRef.current) {
        searchInitiatedByEditRef.current = false;
      }
    }
  }, [filterFromCache]);

  return {
    serialSearch,
    setSerialSearch,
    suggestions,
    setSuggestions,
    isSearching,
    setIsSearching,
    hasSearched,
    setHasSearched,
    suppressNoResultsMessage,
    setSuppressNoResultsMessage,
    isEditingStep1Ref,
    previousEquipmentRef,
    searchInitiatedByEditRef,
    lastSuccessfulSearchRef,
    equipmentCacheRef,
    serialInputRef,
    searchSerialNumber,
    filterFromCache
  };
};

